import type { ReactNode } from 'react';
import { Badge } from '../../../core/badge';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import { formatOptionLetter, sortByOrder } from './exercise-submission-helpers';
import { useUserType } from '../../../../feature-flags/use-user-type';

// ---------------------------------------------------------------------------
// Main evaluation renderer
// ---------------------------------------------------------------------------

export function EvaluationRenderer({
  evaluation,
  content,
}: {
  evaluation: Record<string, unknown>;
  content: StudentExerciseContent;
}) {
  if (!evaluation) {
    return <p className="text-sm text-gray-600">Sem feedback automatico disponivel.</p>;
  }

  const auto = evaluation.auto as Record<string, unknown> | undefined;
  const ai = evaluation.ai as Record<string, unknown> | undefined;

  if (!auto && !ai) {
    return <p className="text-sm text-gray-600">Sem feedback automatico disponivel.</p>;
  }

  return (
    <div className="space-y-3 text-sm text-gray-700">
      {auto && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Correcao automatica</p>
          <AutoEvaluationSummary auto={auto} content={content} />
        </div>
      )}
      {ai && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Feedback da IA</p>
          <AiEvaluationSummary ai={ai} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auto evaluation summary + details
// ---------------------------------------------------------------------------

function AutoEvaluationSummary({
  auto,
  content,
}: {
  auto: Record<string, unknown>;
  content: StudentExerciseContent;
}) {
  const badges: ReactNode[] = [];

  const score = typeof auto.score === 'number' ? auto.score : null;
  const maxScore = typeof auto.maxScore === 'number' ? auto.maxScore : null;
  const isFullyCorrect =
    typeof auto.isFullyCorrect === 'boolean'
      ? auto.isFullyCorrect
      : typeof auto.score === 'number' && typeof auto.maxScore === 'number'
        ? auto.score === auto.maxScore
        : null;

  if (score !== null && maxScore !== null) {
    badges.push(
      <Badge key="auto-score" variant="outline" className="border-gray-200 bg-gray-50 text-gray-700">
        Nota: {score}/{maxScore}
      </Badge>,
    );
  }

  if (isFullyCorrect !== null) {
    badges.push(
      <Badge
        key="auto-status"
        variant="outline"
        className={
          isFullyCorrect ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'
        }
      >
        {isFullyCorrect ? 'Correto' : 'Revisar'}
      </Badge>,
    );
  }

  const details = auto.details as Record<string, unknown> | undefined;

  return (
    <div className="space-y-2">
      {badges.length > 0 && <div className="flex flex-wrap gap-2 text-xs">{badges}</div>}
      {details && details.kind !== 'FillInBlank' && details.kind !== 'SelectForBlank' && (
        <AutoEvaluationDetails details={details} content={content} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auto evaluation details (per exercise kind)
// ---------------------------------------------------------------------------

function AutoEvaluationDetails({
  details,
  content,
}: {
  details: Record<string, unknown>;
  content: StudentExerciseContent;
}) {
  switch (details.kind) {
    case 'MultipleChoice':
      return <MultipleChoiceEvalDetails details={details} content={content} />;
    case 'FillInBlank':
      return <FillInBlankEvalDetails details={details} />;
    case 'SelectForBlank':
      return <SelectForBlankEvalDetails details={details} />;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Multiple choice evaluation details
// ---------------------------------------------------------------------------

function MultipleChoiceEvalDetails({
  details,
  content,
}: {
  details: Record<string, unknown>;
  content: StudentExerciseContent;
}) {
  const options = sortByOrder(content.options ?? []);
  const selectedIndices = Array.isArray(details.selectedOptionIndices)
    ? details.selectedOptionIndices
    : Array.isArray((details as any).optionEvaluations)
      ? (details as any).optionEvaluations
          .filter((evaluation: any) => evaluation.isSelected)
          .map((evaluation: any) => evaluation.index)
      : [];
  const correctIndices = Array.isArray(details.correctOptionIndices) ? details.correctOptionIndices : [];
  const selectedLabels = selectedIndices.length
    ? selectedIndices
        .map((index) => options[index]?.label ?? `Opcao ${formatOptionLetter(index)}`)
        .join(', ')
    : '\u2014';
  const correctLabels = correctIndices
    .map((index) => options[index]?.label)
    .filter((label): label is string => Boolean(label));
  const optionEvaluations = Array.isArray((details as any).optionEvaluations)
    ? (details as any).optionEvaluations
    : [];

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
      <p>
        Selecionada: <span className="font-medium text-gray-900">{selectedLabels}</span>
      </p>
      <p className="text-xs text-gray-500">
        Correta:{' '}
        {correctLabels.length
          ? correctLabels.join(', ')
          : correctIndices.map((index) => formatOptionLetter(index)).join(', ') || '\u2014'}
      </p>
      {optionEvaluations.length > 0 && (
        <div className="mt-2 space-y-1 text-xs text-gray-600">
          {optionEvaluations.map((evaluation: any) => {
            const label = options[evaluation.index]?.label ?? `Opcao ${formatOptionLetter(evaluation.index)}`;
            const { badgeClass, badgeText } = resolveOptionEvalBadge(evaluation);
            return (
              <div key={`${content.id}-eval-${evaluation.index}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-white bg-white px-2 py-1.5">
                <p className="font-medium text-gray-800">{label}</p>
                <Badge variant="outline" className={badgeClass}>{badgeText}</Badge>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function resolveOptionEvalBadge(evaluation: { isSelected: boolean; isCorrectSelection?: boolean; isCorrectAnswer?: boolean }) {
  if (evaluation.isSelected && evaluation.isCorrectSelection) {
    return { badgeClass: 'border-emerald-200 bg-emerald-50 text-emerald-700', badgeText: 'Correta' };
  }
  if (evaluation.isSelected && !evaluation.isCorrectSelection) {
    return { badgeClass: 'border-rose-200 bg-rose-50 text-rose-700', badgeText: 'Resposta incorreta' };
  }
  if (!evaluation.isSelected && evaluation.isCorrectAnswer) {
    return { badgeClass: 'border-amber-200 bg-amber-50 text-amber-700', badgeText: 'Faltou selecionar' };
  }
  return { badgeClass: 'border-slate-200 bg-slate-50 text-slate-700', badgeText: 'Nao selecionada' };
}

// ---------------------------------------------------------------------------
// FillInBlank evaluation details
// ---------------------------------------------------------------------------

function FillInBlankEvalDetails({ details }: { details: Record<string, unknown> }) {
  const sentences = Array.isArray(details.sentences) ? details.sentences : [];
  // Gender-aware label: the viewing student's own submissions are the ones
  // rendered here, so the viewer's gender resolves the addressing. Defaults to
  // the feminine form (women-first platform).
  const { gender } = useUserType();
  const answerLabel = gender === 'male' ? 'Resposta do aluno' : 'Resposta da aluna';
  return (
    <div className="space-y-2 text-sm text-gray-700">
      {sentences.map((sentence: any, index: number) => (
        <div key={sentence.sentenceId ?? `detail-${index}`} className="rounded-md border border-gray-200 bg-white p-2">
          <p className="text-xs font-medium text-gray-600">Sentenca {index + 1}</p>
          <p className="text-xs text-gray-500">
            {answerLabel}: <span className="font-medium text-gray-900">{sentence.providedAnswer || '\u2014'}</span>
          </p>
          <p className="text-xs text-gray-500">
            Correta: <span className="font-medium text-gray-900">{sentence.expectedAnswer || '\u2014'}</span>
          </p>
          <Badge
            variant="outline"
            className={
              sentence.isCorrect
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-amber-200 bg-amber-50 text-amber-700'
            }
          >
            {sentence.isCorrect ? 'Correto' : 'Revisar'}
          </Badge>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SelectForBlank evaluation details
// ---------------------------------------------------------------------------

function SelectForBlankEvalDetails({ details }: { details: Record<string, unknown> }) {
  const sentences = Array.isArray(details.sentences) ? details.sentences : [];
  return (
    <div className="space-y-2 text-sm text-gray-700">
      {sentences.map((sentence: any, sentenceIdx: number) => (
        <div key={sentence.sentenceId ?? `auto-sentence-${sentenceIdx}`} className="rounded-md border border-gray-200 bg-white p-2">
          <p className="text-xs font-medium text-gray-600">Sentenca {sentenceIdx + 1}</p>
          <div className="mt-1 space-y-2">
            {(sentence.blanks ?? []).map((blank: any, blankIdx: number) => (
              <div
                key={blank.blankId ?? `auto-blank-${sentenceIdx}-${blankIdx}`}
                className="rounded-md border border-gray-100 bg-gray-50 p-2"
              >
                <p className="text-xs text-gray-500">
                  Lacuna {blankIdx + 1}:{' '}
                  <span className="font-medium text-gray-900">
                    {typeof blank.selectedOptionIndex === 'number'
                      ? `Opcao ${formatOptionLetter(blank.selectedOptionIndex)}`
                      : '\u2014'}
                  </span>
                </p>
                <p className="text-xs text-gray-500">
                  Correta:{' '}
                  {typeof blank.correctOptionIndex === 'number'
                    ? `Opcao ${formatOptionLetter(blank.correctOptionIndex)}`
                    : '\u2014'}
                </p>
                <Badge
                  variant="outline"
                  className={
                    blank.isCorrect
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-amber-200 bg-amber-50 text-amber-700'
                  }
                >
                  {blank.isCorrect ? 'Correto' : 'Revisar'}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AI evaluation summary
// ---------------------------------------------------------------------------

function AiEvaluationSummary({ ai }: { ai: Record<string, unknown> }) {
  const score = typeof ai.score === 'number' ? ai.score : null;
  const maxScore = typeof ai.maxScore === 'number' ? ai.maxScore : null;
  const passingScore = typeof ai.passingScore === 'number' ? ai.passingScore : null;
  const feedback = typeof ai.feedback === 'string' ? ai.feedback.trim() : '';
  const annotatedText = typeof ai.annotatedSubmission === 'string' ? ai.annotatedSubmission.trim() : '';
  const messages = Array.isArray(ai.messages) ? ai.messages : [];

  const badges: ReactNode[] = [];

  if (score !== null && maxScore !== null) {
    badges.push(
      <Badge key="ai-score" variant="outline" className="border-gray-200 bg-gray-50 text-gray-700">
        Nota: {score}/{maxScore}
      </Badge>,
    );
  }

  if (passingScore !== null) {
    badges.push(
      <Badge key="ai-pass" variant="outline" className="border-gray-200 bg-gray-50 text-gray-700">
        Minimo: {passingScore}
      </Badge>,
    );
  }

  return (
    <div className="space-y-2">
      {badges.length > 0 && <div className="flex flex-wrap gap-2 text-xs">{badges}</div>}
      {messages.length > 0 ? (
        <ul className="space-y-1 text-sm text-gray-700">
          {messages.map((message: any, index: number) => (
            <li key={message.title ?? `ai-message-${index}`}>
              <p className="font-medium text-gray-900">{message.title ?? 'Feedback'}</p>
              {message.description && <p className="text-gray-600">{message.description}</p>}
              {message.annotatedText && (
                <p className="whitespace-pre-wrap text-xs text-gray-500">{message.annotatedText}</p>
              )}
            </li>
          ))}
        </ul>
      ) : feedback ? (
        <p className="whitespace-pre-wrap">{feedback}</p>
      ) : annotatedText ? (
        <p className="whitespace-pre-wrap text-xs text-gray-500">{annotatedText}</p>
      ) : (
        <p className="text-sm text-gray-600">Sem feedback textual.</p>
      )}
    </div>
  );
}
